/**
 * O quadro que atravessa a fronteira, nos dois sentidos.
 *
 * O WebView só sabe transportar **string**: o web chama
 * `window.ReactNativeWebView.postMessage(string)` e a casca responde injetando JavaScript. Todo
 * o resto — tipos, correlação de resposta, erro — é convenção nossa, e é isto que a declara.
 *
 * `id` existe porque o canal é assíncrono e sem ordem garantida: duas requisições em voo voltam
 * na ordem em que a casca terminar, não na ordem em que saíram. Sem correlacionar por id, a
 * resposta de uma vira a resposta da outra — e o sintoma seria um bug intermitente e não
 * reproduzível.
 */
export interface BridgeMessage<TType extends string = string, TPayload = unknown> {
  readonly id: string;
  readonly type: TType;
  readonly payload: TPayload;
}

/**
 * A resposta da casca a uma [BridgeMessage].
 *
 * `ok: false` com `error` preenchido é **estado normal**, não exceção: o usuário cancela o
 * seletor de compartilhamento, nega uma permissão, ou o login nativo falha. Quem chama trata os
 * dois ramos; quem não trata pelo menos vê a mensagem.
 */
export interface BridgeReply<TPayload = unknown> {
  readonly id: string;
  readonly ok: boolean;
  readonly payload: TPayload | null;
  readonly error: string | null;
}

/**
 * O nome do evento que a casca dispara no `window` do WebView ao injetar a resposta.
 *
 * É um `CustomEvent` e não uma função global por um motivo prático: várias partes do web podem
 * querer escutar a mesma resposta (um provider e um teste, por exemplo), e função global só tem
 * um dono.
 */
export const BRIDGE_REPLY_EVENT = 'pintrack:bridge-reply';

/** O mesmo, para mensagens que a casca inicia sem ninguém ter pedido — back de hardware, sessão. */
export const BRIDGE_PUSH_EVENT = 'pintrack:bridge-push';
